import { useEffect, useRef, useState } from "react";
import { LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { DieUnloadReasonAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import Button from "../../common/Button";
import { RegistryHeader } from "../../common/Shared";

const DieUnloadReason = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    reason: "",
    created_By: userId,
  });
  const [loading, setLoading] = useState(false);
  const [iscategoriesData, setIscategoriesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchCategoriesData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await DieUnloadReasonAPI.getAllDieUnloadReason(
        currentPage,
        pageSize,
      );

      const newItems = res.data;

      if (currentPage === 1) {
        setIscategoriesData(newItems);
      } else {
        setIscategoriesData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching alloy data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchCategoriesData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(iscategoriesData);
    } else {
      const filtered = iscategoriesData.filter((d) =>
        d.reason?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, iscategoriesData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      id: 0,
      reason: "",
      created_By: userId,
    });
  };

  const handleEdit = async (id) => {
    try {
      const data = await DieUnloadReasonAPI.getById(id);
      if (data.data) {
        setFormData({
          id: data.data.id,
          reason: data.data.reason,
          Modify_By: userId,
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch alloy details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await DieUnloadReasonAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchCategoriesData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    try {
      await DieUnloadReasonAPI.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      fetchCategoriesData();
      setFormData({
        id: 0,
        reason: "",
        created_By: userId,
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    }
  };

  const fields = [
    {
      name: "reason",
      label: "Reason",
      type: "text",
      required: true,
      errorMessage: "Name is required",
    },
  ];

  return (
    <>
    <div className="flex flex-col h-full bg-white font-plus">
      <RegistryHeader 
        title="Die Unload Reason Registry" 
        subTitle="Extraction Logic Protocol" 
        badge="Quality Assurance Control"
      >
        <div className="relative w-full sm:w-80 group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
            <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
          </div>
          <input
            type="text"
            placeholder="Search by REASON..."
            className="block w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
            >
              <MdClose size={18} />
            </button>
          )}
        </div>

        <button
          onClick={handleAdd}
          className="bg-[#3F80F7] text-white font-black text-[10px] uppercase tracking-[3px] px-8 py-3 border border-[#3F80F7] hover:bg-[#2c5ebc] transition-all flex items-center gap-3"
        >
          <HiOutlinePlus size={16} /> Add REASON
        </button>
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >
          <div className="min-w-max">
            {loading && <Loader centered />}
            <Table
              columns={[{ key: "reason", label: "Reason" }]}
              data={filteredData}
              onEdit={(row) => handleEdit(row.id)}
              onDelete={(row) => handleDelete(row.id)}
              tableClassName="text-[12px] font-bold"
            />
          </div>
        </div>
      </div>
    </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit DieUnload Reason" : "Add DieUnload Reason"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default DieUnloadReason;
